import { FormField, FormLabel, PlusIcon, Select, SelectOptionProps } from '@acceso/design-system';
import { useCallback, useMemo, useState } from 'react';
import { Controller, FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useGetLabels } from '@/features/labels/api/get-labels';
import { usePostLabel } from '@/features/labels/api/post-label';

type TInputLabelsProps<T extends FieldValues> = {
  form: UseFormReturn<T>;
  name: Path<T>;
  label: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  classname?: string;
};

export function InputLabels<T extends FieldValues>({
  label,
  name,
  placeholder,
  form,
  disabled = false,
  required,
  classname,
}: TInputLabelsProps<T>) {
  const { t } = useTranslation();
  const { control } = form;
  const labelSuffix = required ? '*' : '';
  const [searchText, setSearchText] = useState('');

  const { data: labelsData, isLoading } = useGetLabels({ search: searchText });
  const { mutateAsync: createLabel } = usePostLabel();

  const handleCreateLabel = useCallback(
    async (inputValue: string) => {
      const newLabel = await createLabel({ name: inputValue });
      return newLabel.id;
    },
    [createLabel]
  );

  const selectOptions: SelectOptionProps[] = useMemo(() => {
    const options: SelectOptionProps[] =
      labelsData?.map(label => ({
        label: label.name,
        value: label.id,
      })) || [];

    return options;
  }, [labelsData, searchText, t]);

  return (
    <FormField className={classname}>
      <FormLabel htmlFor={name}>
        {label}
        {labelSuffix}
      </FormLabel>
      <Controller
        disabled={disabled}
        control={control}
        name={name}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          return (
            <>
              <Select
                disabled={disabled}
                placeholder={placeholder || label}
                value={value || []}
                multiple={true}
                onChange={onChange}
                options={selectOptions}
                hasError={!!error}
                additionalButtons={
                  searchText &&
                  !isLoading &&
                  !selectOptions.find(({ label }) => label === searchText)
                    ? [
                        {
                          label: `${t('Añadir')} "${searchText}"`,
                          onClick: async () => {
                            const labelId = await handleCreateLabel(searchText);
                            setSearchText('');
                            onChange([...value, labelId]);
                          },
                          icon: PlusIcon,
                        },
                      ]
                    : undefined
                }
                onFilterTextChange={setSearchText}
                withFilter={true}
              />
            </>
          );
        }}
      />
    </FormField>
  );
}
